import React, { ChangeEvent, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { Typography, Grid, GridItem, Box, Heading } from '@tq/ui-components';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import {
  FormProvider,
  ValidatedTextArea,
  Form,
} from '@tq/ui-components/dist/components/forms-engine';

import { BANNER_TIMEOUT } from 'config';
import { dfiReportUpdateValuesState } from 'store';
import { useUpdateDfiAnnualReport, UpdateDfiReportRequest } from 'api';
import { ReviewResponse } from '../../types';
import { ReviewCardFooter } from './ReviewCardFooter';
import { useEditingReviewState, useReviewResponseState } from '../../hooks';

export type ReviewCardProps = {
  question: string;
  idReference: ReviewResponse;
};

export const ReviewCard: React.FC<ReviewCardProps> = ({ question, idReference }) => {
  const { t } = useTranslation('dfi_report_review');

  const updateDfiReportStoreValues = useRecoilValue(dfiReportUpdateValuesState);

  const [isEditing, setIsEditing] = useEditingReviewState(idReference);
  const [response] = useReviewResponseState(idReference);

  const { mutate: updateDfiAnnualMutate, isLoading } = useUpdateDfiAnnualReport();

  const [editedAnswer, setEditedAnswer] = useState<string>(response);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  const backgroundColour = (): string => {
    if (isEditing) return COLOURS.blue[200];
    return isSuccess ? COLOURS.neutral[11] : COLOURS.gray[200];
  };

  const borderColour = (): string => {
    return isSuccess ? COLOURS.green[500] : 'unset';
  };

  const handleSubmit = () => {
    let request: UpdateDfiReportRequest = {
      completed: false,
      ...updateDfiReportStoreValues,
    };

    if (editedAnswer) {
      if (idReference === ReviewResponse.BestPracticeResponse)
        request = { ...request, bestPracticeResponse: editedAnswer };

      if (idReference === ReviewResponse.InfoOnLateResponse)
        request = { ...request, infoOnLateResponse: editedAnswer };

      updateDfiAnnualMutate(request, {
        onSuccess: () => {
          setIsEditing(false);
          setIsSuccess(true);
          setTimeout(() => setIsSuccess(false), BANNER_TIMEOUT);
        },
        onError: () => {
          setIsEditing(false);
        },
      });
    } else {
      setIsEditing(false);
    }
  };

  useEffect(() => {
    if (!isEditing) {
      setEditedAnswer(response);
    }
  }, [isEditing, response]);

  return (
    <>
      <Grid
        borderRadius={5}
        backgroundColor={backgroundColour()}
        border={`1px solid ${borderColour()}`}
        data-testid={`review-response-card-${idReference}`}
        gridGap={8}
        px={5}
        pt={5}
        pb={8}
      >
        <GridItem borderTopRadius={5}>
          <Heading size='s' as='h3'>
            {question}
          </Heading>
        </GridItem>
        <GridItem>
          <Box p={5} backgroundColor={COLOURS.white}>
            {isEditing ? (
              <FormProvider>
                <Form id={`dfi-review-${idReference}-form`} onValidSubmit={handleSubmit}>
                  <ValidatedTextArea
                    data-testid={`review-response-card-${idReference}-textarea`}
                    defaultValue={editedAnswer}
                    id='responseText'
                    label={t('response_text_label', 'Response')}
                    mode='onChange'
                    onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                      setEditedAnswer(event.target.value)
                    }
                    placeholder={t('response_text_placeholder', 'Enter your response here')}
                    validation={{
                      maxLength: {
                        value: 500,
                        message: t(
                          'reason_text_length_error',
                          'You have exceeded the character limit'
                        ),
                      },
                    }}
                  />
                </Form>
              </FormProvider>
            ) : (
              <>
                <Box mt={{ base: 4, lg: 'unset' }}>
                  <strong>{t('your_response', 'Your response')}</strong>
                </Box>
                <Typography py={2} wordBreak='break-word'>
                  <p>{response}</p>
                </Typography>
              </>
            )}
          </Box>
        </GridItem>
        <GridItem>
          <ReviewCardFooter idReference={idReference} isLoading={isLoading} />
        </GridItem>
      </Grid>
    </>
  );
};
